import React, { useEffect, useRef } from "react"
import { CARDS_FLIPPED_EVENT } from "../util/util"

interface Star {
    x: number
    y: number
    z: number
    shade: number
}

function rand(min: number, max: number) {
    return min + Math.random() * (max - min)
}

export default function StarfieldBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const starColor = "#ffffff"

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) {
            return
        }
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            return
        }

        let width = 0
        let height = 0
        let raf = 0
        let running = true
        let last = performance.now()
        let burst = 0
        const cruise = 0.038
        const stars: Star[] = []

        function starCount() {
            const area = Math.max(1, width * height)
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            return Math.round(Math.min(900, Math.max(80, (area * dpr) / 6200)))
        }

        function syncStars() {
            const count = starCount()
            while (stars.length < count) {
                stars.push(spawnStar(true))
            }
            if (stars.length > count) {
                stars.length = count
            }
        }

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            width = window.innerWidth
            height = window.innerHeight
            canvas!.width = Math.floor(width * dpr)
            canvas!.height = Math.floor(height * dpr)
            canvas!.style.width = `${width}px`
            canvas!.style.height = `${height}px`
            ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
            syncStars()
        }

        function spawnStar(anywhere: boolean): Star {
            let x = rand(-1, 1)
            let y = rand(-1, 1)
            const r2 = x * x + y * y
            if (r2 < 0.07) {
                const scale = Math.sqrt(0.07 / Math.max(r2, 0.0001))
                x *= scale
                y *= scale
            }
            return {
                x,
                y,
                z: anywhere ? rand(0.18, 1) : rand(0.88, 1.18),
                shade: rand(0.55, 1),
            }
        }

        function project(star: Star, z: number) {
            const fov = Math.min(width, height) * 0.52
            const depth = Math.max(z, 0.04)
            return {
                x: width * 0.5 + (star.x / depth) * fov,
                y: height * 0.5 + (star.y / depth) * fov,
            }
        }

        function tick(now: number) {
            if (!running) {
                return
            }
            const dt = Math.min(0.033, (now - last) / 1000)
            last = now
            burst *= Math.exp(-dt * 0.95)
            const speed = cruise * (1 + burst * 16)

            ctx!.clearRect(0, 0, width, height)
            ctx!.fillStyle = starColor

            for (const star of stars) {
                star.z -= speed * dt
                if (star.z <= 0.04) {
                    Object.assign(star, spawnStar(false))
                    continue
                }
                const cur = project(star, star.z)
                const near = 1 - Math.min(1, Math.max(0, (star.z - 0.04) / 0.96))
                const size = (0.5 + near * 2.4 * star.shade) * 1.3
                const margin = Math.max(6, size + 2)
                if (
                    cur.x < -margin || cur.x > width + margin ||
                    cur.y < -margin || cur.y > height + margin
                ) {
                    Object.assign(star, spawnStar(false))
                    continue
                }

                const alpha = (0.18 + near * 0.78) * star.shade
                ctx!.globalAlpha = alpha
                ctx!.beginPath()
                ctx!.arc(cur.x, cur.y, size, 0, Math.PI * 2)
                ctx!.fill()
            }

            raf = requestAnimationFrame(tick)
        }

        function onCardsFlipped(e: Event) {
            if ((e as CustomEvent).detail?.showingFront === false) {
                return
            }
            burst = 1
        }

        resize()
        window.addEventListener("resize", resize)
        window.addEventListener(CARDS_FLIPPED_EVENT, onCardsFlipped)
        last = performance.now()
        raf = requestAnimationFrame(tick)

        return () => {
            running = false
            cancelAnimationFrame(raf)
            window.removeEventListener("resize", resize)
            window.removeEventListener(CARDS_FLIPPED_EVENT, onCardsFlipped)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className="falling-leaves-canvas"
            aria-hidden
        />
    )
}
